import { EntityBase, Var } from "addeus-common-library/stores/firestore";
import moment from "moment-with-locales-es6";
import { Familiarity } from "../../utils/profile";
import type { Tone } from "../profile";
import { PlaceType } from "../profile";
import type { PostMood } from "./post";
import type { SocialProfileNetwork } from "./socialProfile";

export enum Period {
    Day = "Day",
    Week = "Week",
    FullPeriod = "FullPeriod",
}

export enum EmojisModifier {
    Plus = "Plus",
    Minus = "Minus",
    Nothing = "Nothing",
}
export enum EventsStatus {
    Yes = "Yes",
    No = "No",
    IDontKnow = "IDontKnow",
}

export enum Objective {
    Fame = "Fame",
    Revenue = "Revenue",
    Likes = "Likes",
}

export enum GenerationStatus {
    NotStarted = "NotStarted",
    Calendar = "Calendar",
    Posts = "Posts",
    Visuals = "Visuals",
    Integration = "Integration",
    Finished = "Finished",
}

export class GeneratedPost extends EntityBase {
    @Var(Boolean)
    selected: boolean = false;

    @Var(Boolean)
    regenerated: boolean = false;

    @Var(moment)
    publishedAt: moment.Moment;

    @Var(String)
    caption: string = "";

    @Var(String)
    mediaPrompt: string = "";

    @Var(String)
    dateTime: string = "";

    @Var(Array.of(String))
    hashtags: string[] = [];

    @Var(String)
    inspiration: string = "";

    @Var(String)
    theme: string = "";

    @Var(Boolean)
    fromUserMedia: boolean = false;

    get hashtagsString(): string {
        return `#${this.hashtags.join(" #")}`;
    }
}

export class Modifiers extends EntityBase {
    @Var(String)
    emojis?: EmojisModifier;

    @Var(Array.of(String))
    themes: PostMood[] = [];

    @Var(String)
    description?: string;

    @Var(Array.of(String))
    tone: Tone[] = [];

    @Var(String)
    additionalInfo?: string;
}

export class UserMediaFile extends EntityBase {
    @Var(String)
    mimeType?: string;

    @Var(String)
    name?: string;

    @Var(String)
    path?: string;

    @Var(Number)
    size?: number;
}

export class PlanningGeneration extends EntityBase {
    @Var(String)
    profession?: string;

    @Var(String)
    passion?: string;

    @Var(String)
    placeName?: string;

    @Var(Number)
    duration?: number;

    @Var(Boolean)
    ended: boolean = false;

    @Var(String)
    activity?: string;

    @Var(String)
    placeType?: PlaceType;

    @Var(Array.of(String))
    tone: Tone[] = [];

    @Var(Number)
    lastStep?: number = 0;

    @Var(Array.of(Number))
    disabledSteps: number[] = [];

    @Var(Familiarity)
    familiarity?: Familiarity;

    @Var(String)
    emoji?: string;

    @Var(Number)
    minPostsPerPeriod?: number;

    @Var(Number)
    maxPostsPerPeriod?: number;

    @Var(Period)
    period?: Period;

    @Var(Boolean)
    helpMeNbPost?: boolean;

    @Var(moment)
    startDate?: moment;

    @Var(moment)
    endDate?: moment;

    @Var(Objective)
    objective?: Objective;

    @Var(Array.of(Number))
    favoriteDays: number[] = [];

    // 0-23
    @Var(Array.of(String))
    favoriteHours: string[] = [];

    @Var(EventsStatus)
    events: EventsStatus = EventsStatus.IDontKnow;

    @Var(String)
    additionalInformation?: string;

    @Var(Array.of(String))
    hashtags: string[] = [];

    @Var(String)
    description?: string;

    @Var(String)
    city?: string;

    @Var(Array.of(String))
    socialNetworks: SocialProfileNetwork[] = [];

    @Var(Array.of(GeneratedPost))
    freshPosts: GeneratedPost[] = [];

    @Var(Array.of(GeneratedPost))
    finalPosts: GeneratedPost[] = [];

    @Var(Boolean)
    hasRegeneration: boolean = false;

    @Var(Modifiers)
    modifiers: Modifiers = new Modifiers();

    @Var(Boolean)
    brandInfoFilled: boolean = false;

    @Var(Boolean)
    brandInfoChanged: boolean = false;

    @Var(Boolean)
    preferencesFilled: boolean = false;

    @Var(Boolean)
    preferencesSkiped: boolean = false;

    @Var(String)
    status: GenerationStatus = GenerationStatus.NotStarted;

    @Var(Array.of(UserMediaFile))
    userMedias: UserMediaFile[] = [];

    @Var(Boolean)
    isUserMediasOrdered: boolean = false;

    @Var(String)
    mediasAspectRatio: string = "1:1";

    @Var(String)
    personalization?: string;

    @Var(String)
    communicationObjective?: string;

    @Var(Boolean)
    isPickUserMediasActive: boolean = false;

    get favoriteDaysString(): string {
        return (
            this.favoriteDays
                // shift sunday to the end of the week
                .sort((a, b) => {
                    if (a === 0) {
                        return 1;
                    }
                    if (b === 0) {
                        return -1;
                    }
                    return a - b;
                })
                .map((day) => {
                    // get week day name from day number with moment
                    return moment().day(day).format("dddd");
                })
                .join(", ")
        );
    }
}
